import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, MenuItem, Paper, Radio, RadioGroup, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import { useParams } from 'react-router-dom';
import api from '@/services/api';
import PageHeader from '@/components/Layout/PageHeader';
import { usePageDocumentTitle } from '@/contexts/PageDocumentTitleContext';
import { playBuzzer, unlockBuzzer } from '@/components/scoreboard/buzzer';
import {
  activeSuspensions,
  addCard,
  breakBeepMark,
  breakRemaining,
  addManualSubclock,
  addOfficial,
  addPlayer,
  addSuspension,
  adjustPlayerGoals,
  adjustScore,
  chooseTimeoutSlot,
  DISPLAY_KEYS,
  canAddCard,
  canAddTwo,
  cardColor,
  type CardKind,
  clockSeconds,
  formatClock,
  formatLongClock,
  formatScore,
  importRoster,
  isCountdown,
  moveOfficial,
  sortOfficialsByNumber,
  matchTitle,
  normalizeDisplay,
  parseRosterText,
  movePlayer,
  moveSubclock,
  normalizeState,
  deputyRemaining,
  endHalfTime,
  eventLabel,
  finishTeamTimeout,
  pauseClock,
  pauseHalfTime,
  penaltyMarks,
  periodDisplayName,
  periodOf,
  periodTimeUp,
  periodScore,
  removeCard,
  removeLastCard,
  removeLastSuspension,
  removeOfficial,
  removePlayer,
  removeSuspension,
  updateSuspension,
  resetMatch,
  resetScoresAndClock,
  resumeHalfTime,
  scoreboardChannel,
  selectPeriod,
  setBreakDuration,
  setBreakRemaining,
  setClockMode,
  setDeputyDuration,
  setDeputyTarget,
  setDisplayPart,
  startDeputy,
  stopDeputy,
  setElapsed,
  setTimeoutRemaining,
  sortPlayersByNumber,
  startClock,
  startHalfTime,
  startTimeout,
  teamScore,
  timeoutSeconds,
  timeoutShown,
  toggleTimeoutShown,
  updateOfficial,
  updatePlayer,
  type DisplayKey,
  type PersonKind,
  type ScoreboardState,
  type TeamSide,
} from '@/components/scoreboard/engine';

const DISPLAY_LABELS: Record<DisplayKey, string> = {
  event: '比賽名稱',
  team: '球隊名稱',
  score: '總比分',
  periodName: '節次名稱',
  periodScores: '各節比分',
  clock: '主時鐘',
  timeout: '暫停 T1–T3',
  suspension: '副時鐘',
  cards: '罰牌',
  players: '球員名單',
};

function stop(event: MouseEvent) {
  event.stopPropagation();
}

function applyShownClock(state: ScoreboardState, shownSec: number): ScoreboardState {
  const shown = Math.max(0, Math.floor(shownSec));
  if (!isCountdown(state)) return setElapsed(state, shown);
  const cap = periodOf(state).durationSec;
  if (cap <= 0) return setElapsed(state, shown);
  return setElapsed(state, Math.max(0, cap - shown));
}

function ClockEditor({
  shownSec,
  onChange,
}: {
  shownSec: number;
  onChange: (shownSec: number) => void;
}) {
  const shown = Math.max(0, Math.floor(shownSec));
  const mins = Math.floor(shown / 60);
  const secs = shown % 60;
  const write = (nextMin: number, nextSec: number) => {
    const minute = Math.max(0, nextMin);
    const second = Math.min(59, Math.max(0, nextSec));
    onChange(minute * 60 + second);
  };
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
      <Button size="small" sx={{ minWidth: 32 }} onClick={() => write(mins - 1, secs)}>−</Button>
      <TextField
        size="small"
        label="分"
        type="number"
        value={mins}
        onChange={(e) => write(Number(e.target.value) || 0, secs)}
        inputProps={{ min: 0, style: { textAlign: 'center', fontWeight: 800 } }}
        sx={{ width: 72 }}
      />
      <Button size="small" sx={{ minWidth: 32 }} onClick={() => write(mins + 1, secs)}>+</Button>
      <Typography fontWeight={800}>:</Typography>
      <Button size="small" sx={{ minWidth: 32 }} onClick={() => write(mins, secs - 1)}>−</Button>
      <TextField
        size="small"
        label="秒"
        type="number"
        value={secs}
        onChange={(e) => write(mins, Number(e.target.value) || 0)}
        inputProps={{ min: 0, max: 59, style: { textAlign: 'center', fontWeight: 800 } }}
        sx={{ width: 72 }}
      />
      <Button size="small" sx={{ minWidth: 32 }} onClick={() => write(mins, secs + 1)}>+</Button>
    </Stack>
  );
}

export default function ScoreboardConsolePage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<ScoreboardState | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [error, setError] = useState<string | null>(null);
  const [moveId, setMoveId] = useState<string | null>(null);
  const [moveNo, setMoveNo] = useState('');
  const [moveError, setMoveError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addSide, setAddSide] = useState<TeamSide>('A');
  const [addNo, setAddNo] = useState('');
  const [addMin, setAddMin] = useState('1');
  const [addSec, setAddSec] = useState('0');
  const [addError, setAddError] = useState<string | null>(null);
  const [rosterTeam, setRosterTeam] = useState<TeamSide | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deputyOpen, setDeputyOpen] = useState(false);
  const [deputyDraft, setDeputyDraft] = useState({ mode: 'time' as 'time' | 'datetime' | 'text', min: '5', sec: '0', endsLocal: '', text: '' });
  const [deputyError, setDeputyError] = useState<string | null>(null);
  const [clockOpen, setClockOpen] = useState(false);
  const [clockDraft, setClockDraft] = useState(0);
  const [timeoutEdit, setTimeoutEdit] = useState<TeamSide | null>(null);
  const [timeoutRemainDraft, setTimeoutRemainDraft] = useState('60');
  const [penaltyId, setPenaltyId] = useState<string | null>(null);
  const [penaltyEditing, setPenaltyEditing] = useState(false);
  const [penaltyNo, setPenaltyNo] = useState('');
  const [penaltyMin, setPenaltyMin] = useState('0');
  const [penaltySec, setPenaltySec] = useState('0');
  const [deskTab, setDeskTab] = useState<'vs' | 'time' | 'score' | 'display'>('vs');
  const [picked, setPicked] = useState<{ team: TeamSide; who: PersonKind; id: string } | null>(null);
  const [importPlayersText, setImportPlayersText] = useState('');
  const [importOfficialsText, setImportOfficialsText] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetChoice, setResetChoice] = useState<'scores' | 'all' | null>(null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const saveTimer = useRef<number | null>(null);
  const loaded = useRef(false);

  usePageDocumentTitle(state ? matchTitle(state) : null);

  useEffect(() => {
    if (!id) return;
    api
      .get<{ match: { state: ScoreboardState } }>(`/scoreboards/${id}`)
      .then((res) => {
        setState(normalizeState(res.data.match.state));
        loaded.current = true;
      })
      .catch(() => setError('找不到比分牌'));
  }, [id]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);

  const commit = useCallback((next: ScoreboardState | ((prev: ScoreboardState) => ScoreboardState)) => {
    setState((prev) => {
      if (!prev) return prev;
      return typeof next === 'function' ? next(prev) : next;
    });
  }, []);

  useEffect(() => {
    if (!id || !state || !loaded.current) return;
    const channel = new BroadcastChannel(scoreboardChannel(id));
    channel.postMessage(state);
    channel.close();
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      api.put(`/scoreboards/${id}`, { state }).catch(() => setError('儲存失敗'));
    }, 400);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [id, state]);

  const horn = useRef({ clock: false, timeout: false, timeoutRemain: -1, breakEnd: false, breakRemain: -1, deputy: false });

  useEffect(() => {
    const unlock = () => unlockBuzzer();
    window.addEventListener('pointerdown', unlock);
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  useEffect(() => {
    if (!state) return;
    const timeUp = state.clockRunning && periodTimeUp(state, now);
    if (timeUp) {
      if (!horn.current.clock) playBuzzer();
      horn.current.clock = true;
      commit((prev) => pauseClock(prev, now));
    } else if (!periodTimeUp(state, now)) {
      horn.current.clock = false;
    }

    const timeoutOn = state.timeoutKind === 'A' || state.timeoutKind === 'B';
    const timeoutRemain = timeoutOn ? Math.max(0, 60 - timeoutSeconds(state, now)) : -1;
    if (timeoutOn && horn.current.timeoutRemain > 10 && timeoutRemain <= 10 && timeoutRemain > 0) playBuzzer();
    horn.current.timeoutRemain = timeoutRemain;

    const timeoutDone = timeoutOn && timeoutRemain <= 0;
    if (timeoutDone) {
      if (!horn.current.timeout) playBuzzer();
      horn.current.timeout = true;
      commit((prev) => finishTeamTimeout(prev));
    } else {
      horn.current.timeout = false;
    }

    const remain = state.breakVisible ? breakRemaining(state, now) : -1;
    const breakEnded = state.breakVisible && state.breakRunning && remain <= 0;
    if (breakEnded) {
      if (!horn.current.breakEnd) playBuzzer();
      horn.current.breakEnd = true;
      commit((prev) => endHalfTime(prev, now));
    } else if (!state.breakVisible) {
      horn.current.breakEnd = false;
    }

    const deputyDone = state.deputyVisible && state.deputyRunning && state.deputyMode !== 'text' && deputyRemaining(state, now) <= 0;
    if (deputyDone) {
      if (!horn.current.deputy) playBuzzer();
      horn.current.deputy = true;
      commit((prev) => stopDeputy(prev, now));
    } else if (!state.deputyVisible || deputyRemaining(state, now) > 0) {
      horn.current.deputy = false;
    }

    const mark = breakBeepMark(state);
    if (mark != null && mark > 0 && horn.current.breakRemain > mark && remain >= 0 && remain <= mark) playBuzzer();
    horn.current.breakRemain = remain;
  }, [commit, now, state]);

  const period = state ? periodOf(state) : null;
  const scoreA = useMemo(() => (state ? teamScore(state, 'A') : null), [state]);
  const scoreB = useMemo(() => (state ? teamScore(state, 'B') : null), [state]);

  if (!state || !period || !scoreA || !scoreB) {
    return <Typography color="text.secondary">{error || '載入中…'}</Typography>;
  }

  const halfA = periodScore(state, 'A');
  const halfB = periodScore(state, 'B');
  const suspensions = activeSuspensions(state, now);
  const timeoutLeft = formatClock(Math.max(0, 60 - timeoutSeconds(state, now)));

  const openDisplay = () => {
    window.open(`/scoreboard/${id}/display`, 'scoreboard-display', 'noopener,width=1600,height=900');
  };

  const stat = (
    label: string,
    count: number,
    color: string,
    onAdd: () => void,
    onRemove: () => void,
    title: string,
    disabled = false
  ) => (
    <Button
      size="small"
      title={title}
      disabled={disabled && count === 0}
      onClick={() => {
        if (!disabled) onAdd();
      }}
      sx={{
        minWidth: 44,
        height: 40,
        px: 0.5,
        fontWeight: 800,
        fontSize: 12,
        lineHeight: 1.05,
        color: count > 0 && label !== '2′' && label !== '4′' ? '#111' : 'text.primary',
        bgcolor: count > 0 && label !== '2′' && label !== '4′' ? color : 'transparent',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
      }}
    >
      {label}
      {count > 0 && (
        <Box component="span" sx={{ display: 'block', fontSize: 13 }}>
          {count}
        </Box>
      )}
      {count > 0 && (
        <Box
          component="span"
          onClick={(e) => {
            stop(e);
            onRemove();
          }}
          sx={{
            position: 'absolute',
            top: -7,
            right: -6,
            width: 16,
            height: 16,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            fontSize: 12,
            lineHeight: '14px',
          }}
        >
          −
        </Box>
      )}
    </Button>
  );

  const display = normalizeDisplay(state.display);

  const timeoutButton = (team: TeamSide) => {
    const running = state.timeoutKind === team;
    const color = team === 'A' ? state.teamAColor : state.teamBColor;
    const slot = chooseTimeoutSlot(state, team, now);
    const exhausted = !running && slot == null;
    const secondLine = running ? timeoutLeft : exhausted ? '已用完' : '暫停';
    return (
      <Stack spacing={0.25}>
        <Button
          size="small"
          onClick={() => {
            setTimeoutEdit(team);
            setTimeoutRemainDraft(String(Math.max(0, 60 - timeoutSeconds(state, now))));
          }}
          sx={{ color, fontWeight: 800, alignSelf: 'center' }}
        >
          編輯暫停
        </Button>
        <Button
          fullWidth
          variant="outlined"
          disabled={exhausted || state.breakVisible}
          onClick={() => commit(startTimeout(state, team, now))}
          sx={{
            minHeight: 72,
            px: 0.5,
            fontWeight: 800,
            fontSize: 18,
            lineHeight: 1.15,
            borderWidth: 2,
            borderColor: color,
            color,
            bgcolor: running ? `${color}22` : 'background.paper',
            '&:hover': { borderWidth: 2, borderColor: color, bgcolor: `${color}33` },
            '&.Mui-disabled': { borderWidth: 2, color, borderColor: color, opacity: 0.35 },
          }}
        >
          <Box>
            <Box>{team}隊</Box>
            <Box sx={{ fontFamily: running ? 'ui-monospace, monospace' : 'inherit', fontSize: running ? 16 : 18 }}>{secondLine}</Box>
          </Box>
        </Button>
      </Stack>
    );
  };

  const personOf = (team: TeamSide, who: PersonKind, id: string) => {
    const list = who === 'player'
      ? (team === 'A' ? state.playersA : state.playersB)
      : (team === 'A' ? state.officialsA : state.officialsB);
    return list.find((p) => p.id === id) || null;
  };

  const pickedPerson = picked ? personOf(picked.team, picked.who, picked.id) : null;

  const giveCard = (team: TeamSide, kind: CardKind) => {
    if (!picked || picked.team !== team || !pickedPerson) return;
    if (!canAddCard(state, team, picked.who, pickedPerson.number, kind)) return;
    commit(addCard(state, team, kind, pickedPerson.number, pickedPerson.name, picked.who));
    setPicked(null);
  };

  const giveSuspension = (team: TeamSide, seconds: number) => {
    if (!picked || picked.team !== team || !pickedPerson) return;
    const units = seconds >= 240 ? 2 : 1;
    if (!canAddTwo(state, team, picked.who, pickedPerson.number, units as 1 | 2)) return;
    commit(addSuspension(state, team, pickedPerson.number, seconds, now, picked.who, pickedPerson.name));
    setPicked(null);
  };

  const actionColumn = (team: TeamSide) => {
    const color = team === 'A' ? state.teamAColor : state.teamBColor;
    const ready = Boolean(picked && picked.team === team && pickedPerson);
    const who = picked?.team === team ? picked.who : 'player';
    const number = ready && pickedPerson ? pickedPerson.number : '';
    const allow = (kind: CardKind) => ready && canAddCard(state, team, who, number, kind);
    const btn = {
      minHeight: 52,
      fontWeight: 800,
      px: 0.5,
      lineHeight: 1.1,
    };
    return (
      <Paper variant="outlined" sx={{ width: 148, flexShrink: 0, p: 0.75, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 0.75, borderTop: `4px solid ${color}` }}>
        <Typography variant="caption" fontWeight={800} textAlign="center" sx={{ color }}>{team}隊</Typography>
        <Button sx={{ ...btn, bgcolor: cardColor('yellow'), color: '#111', '&:hover': { bgcolor: cardColor('yellow') } }} disabled={!allow('yellow')} onClick={() => giveCard(team, 'yellow')}>罰牌 W</Button>
        <Button sx={{ ...btn, bgcolor: cardColor('red'), color: '#fff', '&:hover': { bgcolor: cardColor('red') } }} disabled={!allow('red')} onClick={() => giveCard(team, 'red')}>罰牌 D</Button>
        <Button sx={{ ...btn, bgcolor: cardColor('blue'), color: '#fff', '&:hover': { bgcolor: cardColor('blue') } }} disabled={!allow('blue')} onClick={() => giveCard(team, 'blue')}>罰牌 DR</Button>
        <Button variant="outlined" sx={btn} disabled={!ready || !canAddTwo(state, team, who, number, 1)} onClick={() => giveSuspension(team, 120)}>罰時 2′</Button>
        {who !== 'official' && (
          <Button variant="outlined" sx={btn} disabled={!ready || !canAddTwo(state, team, who, number, 2)} onClick={() => giveSuspension(team, 240)}>罰時 4′</Button>
        )}
        <Button variant="outlined" sx={{ ...btn, minHeight: 56 }} disabled={periodScore(state, team).goals <= 0} onClick={() => commit(adjustScore(state, team, -1))}>−1</Button>
        <Button variant="contained" sx={{ ...btn, minHeight: 72, fontSize: 22, bgcolor: color, '&:hover': { bgcolor: color } }} onClick={() => commit(adjustScore(state, team, 1))}>+1</Button>
        <Box sx={{ flex: 1 }} />
        {timeoutButton(team)}
        <Typography variant="caption" color="text.secondary" textAlign="center">先點選再罰牌</Typography>
      </Paper>
    );
  };

  const markSquare = (count: number, bg: string, ink = '#fff') => (
    <Box
      sx={{
        width: 22,
        height: 22,
        justifySelf: 'center',
        borderRadius: '3px',
        border: '1px solid',
        borderColor: count > 0 ? bg : 'divider',
        bgcolor: count > 0 ? bg : 'transparent',
        color: ink,
        display: 'grid',
        placeItems: 'center',
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      {count > 1 ? count : ''}
    </Box>
  );

  const playerColumn = (team: TeamSide) => {
    const color = team === 'A' ? state.teamAColor : state.teamBColor;
    const players = team === 'A' ? state.playersA : state.playersB;
    const officials = team === 'A' ? state.officialsA : state.officialsB;
    const teamGoals = teamScore(state, team).goals;
    const playerGoals = players.reduce((sum, player) => sum + (player.goals || 0), 0);
    const gap = teamGoals - playerGoals;
    const playerColumns = '36px minmax(0,1fr) 78px 28px 24px 24px 28px 34px';
    const officialColumns = '36px minmax(0,1fr) 28px 28px 28px 34px';
    const headText = (label: string, span = 1) => (
      <Typography variant="caption" fontWeight={800} color="text.secondary" textAlign="center" sx={{ gridColumn: span > 1 ? `span ${span}` : undefined, lineHeight: 1 }}>
        {label}
      </Typography>
    );
    const rowSx = (who: PersonKind, selected: boolean) => ({
      display: 'grid',
      gridTemplateColumns: who === 'player' ? playerColumns : officialColumns,
      columnGap: 0.25,
      alignItems: 'center',
      minWidth: 0,
      px: 0.75,
      py: 0.55,
      cursor: 'pointer',
      borderBottom: '1px solid',
      borderColor: 'divider',
      boxShadow: selected ? `inset 3px 0 0 ${color}` : 'none',
      bgcolor: selected ? 'action.selected' : 'transparent',
    });
    const row = (who: PersonKind, person: { id: string; number: string; name: string; goals?: number }) => {
      const marks = penaltyMarks(state, team, who, person.number);
      const selected = picked?.team === team && picked.who === who && picked.id === person.id;
      const goals = person.goals || 0;
      return (
        <Box key={person.id} onClick={() => setPicked({ team, who, id: person.id })} sx={rowSx(who, selected)}>
          <Typography fontWeight={800} sx={{ fontFamily: 'ui-monospace, monospace' }}>{person.number || '—'}</Typography>
          <Typography noWrap fontWeight={700} sx={{ minWidth: 0 }}>{person.name || '未填姓名'}</Typography>
          {who === 'player' && (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
              <Button size="small" sx={{ minWidth: 22, width: 22, px: 0 }} disabled={goals <= 0} onClick={(e) => { stop(e); commit(adjustPlayerGoals(state, team, person.id, -1)); setPicked(null); }}>−</Button>
              <Typography fontWeight={800} sx={{ minWidth: 16, textAlign: 'center' }}>{goals}</Typography>
              <Button size="small" variant="contained" disabled={gap <= 0} sx={{ minWidth: 22, width: 22, px: 0, bgcolor: color, color: '#fff', '&:hover': { bgcolor: color }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'text.disabled' } }} onClick={(e) => { stop(e); commit(adjustPlayerGoals(state, team, person.id, 1)); setPicked(null); }}>+</Button>
            </Stack>
          )}
          {markSquare(marks.w, cardColor('yellow'), '#111')}
          {markSquare(marks.two >= 1 ? 1 : 0, '#9aa0a6', '#111')}
          {who === 'player' && markSquare(marks.two >= 2 ? 1 : 0, '#9aa0a6', '#111')}
          {markSquare(marks.d, cardColor('red'))}
          {markSquare(marks.dr, cardColor('blue'))}
        </Box>
      );
    };
    return (
      <Paper variant="outlined" sx={{ flex: 1, minWidth: 420, p: 1, borderRadius: 2, borderTop: `4px solid ${color}`, display: 'flex', flexDirection: 'column', minHeight: 520 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography fontWeight={800} sx={{ color }}>{team === 'A' ? state.teamAName : state.teamBName}</Typography>
          <Button size="small" onClick={() => setRosterTeam(team)}>編輯名單</Button>
        </Stack>
        <Typography variant="caption" color={gap === 0 ? 'text.secondary' : 'warning.main'}>
          球隊 {teamGoals} · 球員 {playerGoals} · 相差 {gap > 0 ? `+${gap}` : gap}
        </Typography>
        <Box sx={{ flex: 1, overflow: 'auto', mt: 0.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: playerColumns, alignItems: 'center', columnGap: 0.25, minWidth: 0, px: 0.75, py: 0.35 }}>
            <Typography variant="caption" fontWeight={800} color="text.secondary">號碼</Typography>
            <Typography variant="caption" fontWeight={800} color="text.secondary">姓名</Typography>
            {headText('球')}
            {headText('W')}
            {headText('2′', 2)}
            {headText('D')}
            {headText('DR')}
          </Box>
          {players.map((p) => row('player', p))}
          <Typography variant="subtitle2" sx={{ mt: 1.25, mb: 0.25, px: 0.75 }}>球隊職員</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: officialColumns, alignItems: 'center', columnGap: 0.25, minWidth: 0, px: 0.75, py: 0.35 }}>
            <Typography variant="caption" fontWeight={800} color="text.secondary">號碼</Typography>
            <Typography variant="caption" fontWeight={800} color="text.secondary">姓名</Typography>
            {headText('W')}
            {headText('2′')}
            {headText('D')}
            {headText('DR')}
          </Box>
          {officials.map((p) => row('official', p))}
        </Box>
      </Paper>
    );
  };

  const liveSide = (team: TeamSide) => {
    const clocks = suspensions.filter((s) => s.team === team);
    const cards = state.cards.filter((c) => c.team === team);
    const color = team === 'A' ? state.teamAColor : state.teamBColor;
    return (
      <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0, minHeight: 180 }}>
        <Typography fontWeight={800} noWrap sx={{ color }}>{team === 'A' ? state.teamAName : state.teamBName}</Typography>
        <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          {clocks.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.25 }}>沒有出場罰時</Typography>
          )}
          {clocks.map((s) => (
            <Box
              key={s.id}
              component="button"
              type="button"
              onClick={() => { setPenaltyEditing(false); setPenaltyId(s.id); }}
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '2.2ch 1fr auto',
                alignItems: 'center',
                columnGap: 1,
                px: 1.25,
                py: 1,
                border: 0,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'transparent',
                color: 'text.primary',
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography fontWeight={800} sx={{ fontFamily: 'ui-monospace, monospace' }}>{s.playerNo}</Typography>
              <Typography fontWeight={800} sx={{ fontFamily: 'ui-monospace, monospace' }}>{formatClock(s.remainingSec)}</Typography>
              <Typography variant="caption" color="text.secondary">{s.manual ? '自訂' : `${Math.round(s.durationSec / 60)}′`}</Typography>
            </Box>
          ))}
        </Box>
        {cards.length > 0 && (
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {cards.map((c) => (
              <Button key={c.id} size="small" onClick={() => commit(removeCard(state, c.id))} sx={{ minWidth: 36, height: 28, px: 0.75, bgcolor: cardColor(c.kind), color: c.kind === 'yellow' ? '#111' : '#fff', '&:hover': { bgcolor: cardColor(c.kind) } }}>
                {c.playerNo}
              </Button>
            ))}
          </Stack>
        )}
        <Button size="small" variant="outlined" onClick={() => { setAddSide(team); setAddError(null); setAddOpen(true); }}>新增罰時</Button>
      </Stack>
    );
  };

  const deputyLabel = state.deputyMode === 'text'
    ? (state.deputyText || '文字')
    : formatLongClock(deputyRemaining(state, now));

  const openDeputyEditor = () => {
    const duration = state.deputyDurationSec || 0;
    let endsLocal = '';
    if (state.deputyEndsAt) {
      const date = new Date(state.deputyEndsAt);
      const pad = (n: number) => String(n).padStart(2, '0');
      endsLocal = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
    setDeputyDraft({
      mode: state.deputyMode === 'datetime' || state.deputyMode === 'text' ? state.deputyMode : 'time',
      min: String(Math.floor(duration / 60)),
      sec: String(duration % 60),
      endsLocal,
      text: state.deputyText || '',
    });
    setDeputyError(null);
    setDeputyOpen(true);
  };

  const applyDeputy = (start: boolean) => {
    let next = state;
    if (deputyDraft.mode === 'text') {
      next = { ...state, deputyMode: 'text', deputyText: deputyDraft.text, deputyRunning: false, deputyStartedAt: null };
    } else if (deputyDraft.mode === 'datetime') {
      const endsAt = new Date(deputyDraft.endsLocal).getTime();
      if (!deputyDraft.endsLocal || Number.isNaN(endsAt)) {
        setDeputyError('請選擇日期和時間');
        return;
      }
      if (endsAt <= Date.now()) {
        setDeputyError('指定時間必須在現在之後');
        return;
      }
      next = setDeputyTarget(state, endsAt);
    } else {
      const total = (Number(deputyDraft.min) || 0) * 60 + (Number(deputyDraft.sec) || 0);
      if (total <= 0) {
        setDeputyError('請設定倒數時間');
        return;
      }
      next = setDeputyDuration(state, total);
    }
    if (start) {
      if (state.breakVisible) {
        setDeputyError('中場休息進行中，請先結束中場');
        return;
      }
      next = startDeputy(next, now);
    } else {
      next = { ...next, deputyVisible: false, deputyRunning: false, deputyStartedAt: null };
    }
    commit(next);
    setDeputyOpen(false);
  };

  const timeoutOn = state.timeoutKind === 'A' || state.timeoutKind === 'B';
  const deputyOn = Boolean(state.deputyVisible);
  const penalty = suspensions.find((s) => s.id === penaltyId) || null;

  return (
    <Box>
      <PageHeader
        icon={<ScoreboardIcon />}
        title="比分控制台"
        backTo="/scoreboard"
        backLabel="賽事比分牌"
        subtitle={matchTitle(state)}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setSettingsOpen(true)}>設定</Button>
            <Button variant="outlined" color="error" onClick={() => { setResetChoice(null); setResetConfirm(false); setResetOpen(true); }}>重設</Button>
            <Button variant="contained" color="secondary" startIcon={<OpenInNewIcon />} onClick={openDisplay}>開啟顯示畫面</Button>
          </Stack>
        }
      />
      {error && <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', overflowX: 'auto', pb: 1 }}>
        {playerColumn('A')}
        {actionColumn('A')}
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, width: 460, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={800} noWrap sx={{ flex: 1, color: state.teamAColor }}>{state.teamAName}</Typography>
            <TextField
              select
              size="small"
              label="節次"
              value={state.periodIndex}
              disabled={state.clockRunning}
              onChange={(e) => commit(selectPeriod(state, Number(e.target.value), now))}
              sx={{ width: 132 }}
            >
              {state.periods.map((p, index) => (
                <MenuItem key={p.id} value={index}>{periodDisplayName(p.name)}</MenuItem>
              ))}
            </TextField>
            <Typography fontWeight={800} noWrap textAlign="right" sx={{ flex: 1, color: state.teamBColor }}>{state.teamBName}</Typography>
          </Stack>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" fontWeight={800} sx={{ lineHeight: 0.9 }}>
              {formatScore(scoreA.goals)} : {formatScore(scoreB.goals)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {periodDisplayName(period.name)} {formatScore(halfA.goals)} : {formatScore(halfB.goals)}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">{state.breakVisible ? '中場休息' : '主時鐘'}</Typography>
            <Typography variant="h2" fontWeight={800} sx={{ fontFamily: 'ui-monospace, monospace', lineHeight: 1, color: (state.breakVisible ? state.breakRunning : state.clockRunning) ? 'text.primary' : '#ed6c02' }}>
              {formatClock(state.breakVisible ? breakRemaining(state, now) : clockSeconds(state, now))}
            </Typography>
            <Button
              size="small"
              onClick={() => {
                setClockDraft(state.breakVisible ? breakRemaining(state, now) : clockSeconds(state, now));
                setClockOpen(true);
              }}
            >
              編輯時間
            </Button>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            disabled={!state.breakVisible && !timeoutOn && !deputyOn && !state.clockRunning && periodTimeUp(state, now)}
            sx={{
              py: 1.4,
              minHeight: 84,
              fontSize: 28,
              fontWeight: 900,
              lineHeight: 1.05,
              ...(!state.breakVisible && timeoutOn
                ? { bgcolor: '#08343c', color: '#3ee0ff', '&:hover': { bgcolor: '#0a4550' } }
                : !state.breakVisible && deputyOn
                  ? { bgcolor: '#0e2c14', color: '#7dff8a', '&:hover': { bgcolor: '#14401c' } }
                  : (state.breakVisible ? state.breakRunning : state.clockRunning)
                    ? { bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }
                    : state.breakVisible && breakRemaining(state, now) <= 0
                      ? { bgcolor: '#5d4037', '&:hover': { bgcolor: '#4e342e' } }
                      : !state.breakVisible && periodTimeUp(state, now)
                        ? { bgcolor: '#bdbdbd', color: 'rgba(255,255,255,0.85)' }
                        : { bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }),
              '&.Mui-disabled': { bgcolor: '#bdbdbd', color: 'rgba(255,255,255,0.8)' },
            }}
            onClick={() => {
              if (state.breakVisible) {
                if (state.breakRunning) commit(pauseHalfTime(state, now));
                else if (breakRemaining(state, now) > 0) commit(resumeHalfTime(state, now));
                else commit(endHalfTime(state, now));
                return;
              }
              if (timeoutOn) {
                commit(startClock(state, now));
                return;
              }
              if (deputyOn) {
                commit(stopDeputy(state, now));
                return;
              }
              commit(state.clockRunning ? pauseClock(state, now) : startClock(state, now));
            }}
          >
            {state.breakVisible
              ? state.breakRunning
                ? '暫停中場'
                : breakRemaining(state, now) > 0
                  ? '繼續中場'
                  : '結束中場'
              : timeoutOn
                ? (
                  <Box>
                    <Box sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 34 }}>{timeoutLeft}</Box>
                    <Box sx={{ fontSize: 15 }}>{state.timeoutKind}隊暫停 · 繼續比賽</Box>
                  </Box>
                )
                : deputyOn
                  ? (
                    <Box>
                      <Box sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 30 }}>{deputyLabel}</Box>
                      <Box sx={{ fontSize: 15 }}>副時鐘 · 關閉</Box>
                    </Box>
                  )
                  : state.clockRunning
                    ? '結束計時'
                    : '開始計時'}
          </Button>
          {state.breakVisible && (
            <Button variant="outlined" color="secondary" onClick={() => commit(endHalfTime(state, now))}>結束中場</Button>
          )}

          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">副時鐘</Typography>
              <Typography noWrap fontWeight={800} sx={{ fontFamily: 'ui-monospace, monospace', color: deputyOn ? '#1b8a24' : 'text.primary' }}>
                {deputyOn ? deputyLabel : '未顯示'}
              </Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={openDeputyEditor}>編輯</Button>
          </Stack>

          <Divider />

          <Typography variant="caption" fontWeight={800} color="text.secondary">出場罰時</Typography>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            {liveSide('A')}
            {liveSide('B')}
          </Stack>
        </Paper>
        {actionColumn('B')}
        {playerColumn('B')}
      </Box>

      <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>事件紀錄</Typography>
        {state.events.length === 0 && <Typography variant="body2" color="text.secondary">沒有事件</Typography>}
        <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
          {state.events.map((event) => (
            <Stack key={event.id} direction="row" spacing={1.5} sx={{ py: 0.4, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ width: 64, fontFamily: 'ui-monospace, monospace', fontWeight: 800 }}>{formatClock(event.atElapsed)}</Typography>
              <Typography sx={{ width: 88 }} color="text.secondary" noWrap>{event.periodName}</Typography>
              <Typography sx={{ width: 48 }} fontWeight={700}>{event.team === 'ref' ? '裁判' : `${event.team}隊`}</Typography>
              <Typography sx={{ flex: 1 }}>{eventLabel(event)}</Typography>
            </Stack>
          ))}
        </Box>
      </Paper>


      <Dialog open={clockOpen} onClose={() => setClockOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{state.breakVisible ? '編輯中場時間' : '編輯主時鐘'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {state.breakVisible ? '這是畫面上的中場剩餘時間。' : '這是畫面上的比賽時間。儲存後主時鐘會停在這個時間。'}
          </Typography>
          <ClockEditor shownSec={clockDraft} onChange={setClockDraft} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClockOpen(false)}>取消</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              commit(state.breakVisible ? setBreakRemaining(state, clockDraft, now) : applyShownClock(state, clockDraft));
              setClockOpen(false);
            }}
          >
            儲存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deputyOpen} onClose={() => setDeputyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>副時鐘</DialogTitle>
        <DialogContent>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={deputyDraft.mode}
            onChange={(_, value) => value && setDeputyDraft((prev) => ({ ...prev, mode: value }))}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="time">類型1 倒數</ToggleButton>
            <ToggleButton value="datetime">類型2 指定時間</ToggleButton>
            <ToggleButton value="text">文字</ToggleButton>
          </ToggleButtonGroup>
          {deputyDraft.mode === 'time' && (
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1}>
                {[5, 10, 15].map((min) => (
                  <Button key={min} fullWidth variant={deputyDraft.min === String(min) && deputyDraft.sec === '0' ? 'contained' : 'outlined'} color="secondary" onClick={() => setDeputyDraft((prev) => ({ ...prev, min: String(min), sec: '0' }))}>
                    {min} 分鐘
                  </Button>
                ))}
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField size="small" type="number" label="分" value={deputyDraft.min} onChange={(e) => setDeputyDraft((prev) => ({ ...prev, min: e.target.value }))} sx={{ flex: 1 }} />
                <TextField size="small" type="number" label="秒" value={deputyDraft.sec} onChange={(e) => setDeputyDraft((prev) => ({ ...prev, sec: e.target.value }))} sx={{ flex: 1 }} />
              </Stack>
            </Stack>
          )}
          {deputyDraft.mode === 'datetime' && (
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="倒數至"
              InputLabelProps={{ shrink: true }}
              value={deputyDraft.endsLocal}
              onChange={(e) => setDeputyDraft((prev) => ({ ...prev, endsLocal: e.target.value }))}
            />
          )}
          {deputyDraft.mode === 'text' && (
            <TextField fullWidth multiline minRows={3} label="顯示文字" value={deputyDraft.text} onChange={(e) => setDeputyDraft((prev) => ({ ...prev, text: e.target.value }))} />
          )}
          {deputyError && <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>{deputyError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeputyOpen(false)}>取消</Button>
          {state.deputyVisible && (
            <Button color="inherit" onClick={() => { commit(stopDeputy(state, now)); setDeputyOpen(false); }}>關閉</Button>
          )}
          <Button variant="outlined" onClick={() => applyDeputy(false)}>儲存</Button>
          <Button variant="contained" color="secondary" onClick={() => applyDeputy(true)}>開始顯示</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1 }}>設定</DialogTitle>
        <DialogContent sx={{ px: 0, pt: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '148px minmax(0, 1fr)' },
              minHeight: { sm: 460 },
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                gap: 0.5,
                p: 1.25,
                borderBottom: { xs: 1, sm: 0 },
                borderRight: { sm: 1 },
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              {([
                ['vs', '對戰'],
                ['time', '時間'],
                ['score', '比分'],
                ['display', '顯示'],
              ] as const).map(([value, label]) => (
                <Button
                  key={value}
                  size="small"
                  variant={deskTab === value ? 'contained' : 'text'}
                  color={deskTab === value ? 'secondary' : 'inherit'}
                  onClick={() => setDeskTab(value)}
                  sx={{ justifyContent: { sm: 'flex-start' }, px: 1.5, fontWeight: 700 }}
                >
                  {label}
                </Button>
              ))}
            </Box>

            <Box sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
            {deskTab === 'vs' && (
              <Stack spacing={2}>
                <TextField size="small" label="比賽名稱" value={state.eventName} onChange={(e) => commit({ ...state, eventName: e.target.value })} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {([
                    ['A', state.teamAName, state.teamAColor, 'teamAName', 'teamAColor'],
                    ['B', state.teamBName, state.teamBColor, 'teamBName', 'teamBColor'],
                  ] as const).map(([side, name, color, nameKey, colorKey]) => (
                    <Paper key={side} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{side}隊</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <TextField
                          size="small"
                          label="名稱"
                          value={name}
                          onChange={(e) => commit({ ...state, [nameKey]: e.target.value })}
                          sx={{ flex: 1 }}
                        />
                        <Box
                          component="input"
                          type="color"
                          aria-label={`${side}隊顏色`}
                          value={color}
                          onChange={(e) => commit({ ...state, [colorKey]: e.target.value })}
                          sx={{ width: 40, height: 40, p: 0.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'transparent', cursor: 'pointer' }}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: 1.5, alignItems: 'center' }}>
                  <TextField size="small" type="number" label="每隊暫停次數" value={state.timeoutsPerTeam} onChange={(e) => commit({ ...state, timeoutsPerTeam: Math.max(1, Number(e.target.value) || 1) })} />
                  <ToggleButtonGroup exclusive fullWidth size="small" value={state.displayLang === 'en' ? 'en' : 'zh'} onChange={(_, value) => value && commit({ ...state, displayLang: value })}>
                    <ToggleButton value="zh">顯示中文</ToggleButton>
                    <ToggleButton value="en">Display English</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Stack>
            )}

            {deskTab === 'time' && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 700 }}>計時方式</Typography>
                  <ToggleButtonGroup exclusive fullWidth size="small" value={state.clockMode === 'down' ? 'down' : state.clockMode === 'custom' ? 'custom' : 'up'} onChange={(_, value) => value && commit(setClockMode(state, value))}>
                    <ToggleButton value="up" disabled={state.clockRunning || state.breakVisible}>正計時</ToggleButton>
                    <ToggleButton value="down" disabled={state.clockRunning || state.breakVisible}>倒計時</ToggleButton>
                    <ToggleButton value="custom" disabled={state.clockRunning || state.breakVisible}>自訂</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 700, textAlign: 'center' }}>
                    {state.breakVisible ? '中場剩餘' : '主時鐘'}
                  </Typography>
                  <ClockEditor
                    shownSec={state.breakVisible ? breakRemaining(state, now) : clockSeconds(state, now)}
                    onChange={(shown) => commit(state.breakVisible ? setBreakRemaining(state, shown, now) : applyShownClock(state, shown))}
                  />
                </Paper>
                {periodTimeUp(state, now) && !state.breakVisible && !state.clockRunning && (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 700 }}>中場休息</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' }, gap: 1.5, alignItems: 'center' }}>
                      <TextField size="small" type="number" label="中場（分）" value={Math.max(1, Math.round(state.breakDurationSec / 60))} onChange={(e) => commit(setBreakDuration(state, (Number(e.target.value) || 1) * 60))} />
                      <ToggleButtonGroup exclusive fullWidth size="small" value={state.breakBeep} onChange={(_, value) => value && commit({ ...state, breakBeep: value })}>
                        <ToggleButton value="off">不響鈴</ToggleButton>
                        <ToggleButton value="min3">3分鐘</ToggleButton>
                        <ToggleButton value="custom">自訂</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    {state.breakBeep === 'custom' && (
                      <TextField size="small" type="number" label="響鈴（分）" value={Math.max(0, Math.round(state.breakBeepSec / 60))} onChange={(e) => commit({ ...state, breakBeepSec: Math.max(0, Number(e.target.value) || 0) * 60 })} sx={{ mt: 1.5, maxWidth: 160 }} />
                    )}
                    <Button variant="outlined" color="secondary" sx={{ mt: 1.5 }} onClick={() => commit(startHalfTime(state, now))}>開始中場</Button>
                  </Paper>
                )}
                <Box>
                  <Button variant="outlined" onClick={() => playBuzzer()}>試喇叭</Button>
                </Box>
              </Stack>
            )}

            {deskTab === 'score' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) 96px 96px', columnGap: 1.5, rowGap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>節次</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontWeight: 700, textAlign: 'center' }}>{state.teamAName}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontWeight: 700, textAlign: 'center' }}>{state.teamBName}</Typography>
                {state.periods.map((p, index) => (
                  <Box key={p.id} sx={{ display: 'contents' }}>
                    <Button size="small" variant={index === state.periodIndex ? 'contained' : 'outlined'} color="secondary" disabled={state.clockRunning} onClick={() => commit(selectPeriod(state, index, now))} sx={{ justifyContent: 'flex-start' }}>{periodDisplayName(p.name)}</Button>
                    <TextField size="small" type="number" label="A" value={p.goalsA} onChange={(e) => {
                      const periods = state.periods.map((item, i) => i === index ? { ...item, goalsA: Math.max(0, Number(e.target.value) || 0) } : item);
                      commit({ ...state, periods });
                    }} />
                    <TextField size="small" type="number" label="B" value={p.goalsB} onChange={(e) => {
                      const periods = state.periods.map((item, i) => i === index ? { ...item, goalsB: Math.max(0, Number(e.target.value) || 0) } : item);
                      commit({ ...state, periods });
                    }} />
                  </Box>
                ))}
              </Box>
            )}

            {deskTab === 'display' && (
              <Stack spacing={1.25}>
                <Typography variant="caption" color="text.secondary">
                  球隊名稱顏色跟各隊顏色。其餘項目可改顏色和大小，並可隱藏。
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                  {DISPLAY_KEYS.map((key) => {
                    const part = display[key];
                    return (
                      <Paper key={key} variant="outlined" sx={{ px: 1, py: 0.75, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={part.visible}
                          inputProps={{ 'aria-label': `顯示${DISPLAY_LABELS[key]}` }}
                          onChange={(e) => commit(setDisplayPart(state, key, { visible: e.target.checked }))}
                        />
                        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{DISPLAY_LABELS[key]}</Typography>
                        <TextField size="small" type="number" label="大小" value={part.scale} inputProps={{ step: 0.1, min: 0.6, max: 2 }} onChange={(e) => commit(setDisplayPart(state, key, { scale: Number(e.target.value) || 1 }))} sx={{ width: 76 }} />
                        {key !== 'team' && (
                          <Box
                            component="input"
                            type="color"
                            aria-label={`${DISPLAY_LABELS[key]}顏色`}
                            value={part.color}
                            onChange={(e) => commit(setDisplayPart(state, key, { color: e.target.value }))}
                            sx={{ width: 36, height: 36, p: 0.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'transparent', cursor: 'pointer', flexShrink: 0 }}
                          />
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Stack>
            )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setSettingsOpen(false)}>關閉</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>重設比賽</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            先選擇重設範圍。確認後不能復原。
          </Typography>
          <RadioGroup value={resetChoice ?? ''} onChange={(e) => setResetChoice(e.target.value as 'scores' | 'all')}>
            <FormControlLabel
              value="scores"
              control={<Radio />}
              label={
                <Box sx={{ py: 0.5 }}>
                  <Typography fontWeight={800}>只重設比分及時鐘</Typography>
                  <Typography variant="body2" color="text.secondary">
                    所有半場比分和球員入球歸零，時鐘回到第一節 00:00，中場休息結束。名單、罰牌、副時鐘、暫停和事件紀錄保留。
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={
                <Box sx={{ py: 0.5 }}>
                  <Typography fontWeight={800}>全部重設</Typography>
                  <Typography variant="body2" color="text.secondary">
                    比分、時鐘、罰牌、副時鐘、暫停、事件紀錄、球員和球隊職員全部清除。球隊名稱保留。
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>取消</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!resetChoice}
            onClick={() => setResetConfirm(true)}
          >
            {resetChoice === 'all' ? '全部重設' : resetChoice === 'scores' ? '只重設比分及時鐘' : '重設'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetConfirm} onClose={() => setResetConfirm(false)} fullWidth maxWidth="xs">
        <DialogTitle>再次確認</DialogTitle>
        <DialogContent>
          <Typography>
            {resetChoice === 'all'
              ? '確定要全部重設嗎？比分、時鐘、罰牌、副時鐘、暫停、事件紀錄、球員和球隊職員會被清除，不能復原。'
              : '確定要只重設比分及時鐘嗎？比分會歸零，時鐘回到 00:00，不能復原。'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirm(false)}>取消</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (resetChoice === 'scores') commit(resetScoresAndClock(state));
              if (resetChoice === 'all') commit(resetMatch(state));
              setResetConfirm(false);
              setResetOpen(false);
              setResetChoice(null);
            }}
          >
            確定重設
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rosterTeam != null} onClose={() => setRosterTeam(null)} fullWidth maxWidth="md">
        {rosterTeam && (() => {
          const teamName = rosterTeam === 'A' ? state.teamAName : state.teamBName;
          const teamColor = rosterTeam === 'A' ? state.teamAColor : state.teamBColor;
          const players = rosterTeam === 'A' ? state.playersA : state.playersB;
          const officials = rosterTeam === 'A' ? state.officialsA : state.officialsB;
          const columns = '72px minmax(0, 1fr) auto';
          return (
            <>
              <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: 1, borderColor: 'divider', py: 1.75 }}>
                <Box sx={{ width: 12, height: 36, borderRadius: 1, bgcolor: teamColor, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" fontWeight={800} noWrap>{teamName || `${rosterTeam}隊`}名單</Typography>
                  <Typography variant="body2" color="text.secondary">{players.length} 名球員 · {officials.length} 名職員</Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => setImportOpen(true)}>匯入名單</Button>
              </DialogTitle>
              <DialogContent sx={{ pt: '16px !important' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                      <Typography fontWeight={800} sx={{ flex: 1 }}>球員</Typography>
                      <Button size="small" onClick={() => commit(sortPlayersByNumber(state, rosterTeam, 'asc'))}>升序</Button>
                      <Button size="small" onClick={() => commit(sortPlayersByNumber(state, rosterTeam, 'desc'))}>降序</Button>
                      <Button size="small" variant="contained" color="secondary" onClick={() => commit(addPlayer(state, rosterTeam))}>加入</Button>
                    </Box>
                    <Box sx={{ px: 1.5, pt: 1, display: 'grid', gridTemplateColumns: columns, columnGap: 0.75, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>號碼</Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>姓名</Typography>
                      <Box />
                    </Box>
                    <Stack spacing={0.75} sx={{ p: 1.5, pt: 0.75 }}>
                      {players.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>尚未加入球員</Typography>
                      )}
                      {players.map((p, index, list) => (
                        <Box key={p.id} sx={{ display: 'grid', gridTemplateColumns: columns, columnGap: 0.75, alignItems: 'center' }}>
                          <TextField size="small" value={p.number} placeholder="#" inputProps={{ 'aria-label': '球員號碼' }} onChange={(e) => commit(updatePlayer(state, rosterTeam, p.id, { number: e.target.value }))} />
                          <TextField size="small" value={p.name} placeholder="姓名" inputProps={{ 'aria-label': '球員姓名' }} onChange={(e) => commit(updatePlayer(state, rosterTeam, p.id, { name: e.target.value }))} />
                          <Stack direction="row" spacing={0}>
                            <IconButton size="small" aria-label="上移" disabled={index === 0} onClick={() => commit(movePlayer(state, rosterTeam, p.id, -1))}><ArrowUpwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" aria-label="下移" disabled={index === list.length - 1} onClick={() => commit(movePlayer(state, rosterTeam, p.id, 1))}><ArrowDownwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" aria-label="刪除球員" onClick={() => commit(removePlayer(state, rosterTeam, p.id))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                      <Typography fontWeight={800} sx={{ flex: 1 }}>球隊職員</Typography>
                      <Button size="small" onClick={() => commit(sortOfficialsByNumber(state, rosterTeam, 'asc'))}>升序</Button>
                      <Button size="small" onClick={() => commit(sortOfficialsByNumber(state, rosterTeam, 'desc'))}>降序</Button>
                      <Button size="small" variant="contained" color="secondary" onClick={() => commit(addOfficial(state, rosterTeam))}>加入</Button>
                    </Box>
                    <Box sx={{ px: 1.5, pt: 1, display: 'grid', gridTemplateColumns: columns, columnGap: 0.75, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>號碼</Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>姓名</Typography>
                      <Box />
                    </Box>
                    <Stack spacing={0.75} sx={{ p: 1.5, pt: 0.75 }}>
                      {officials.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>尚未加入職員</Typography>
                      )}
                      {officials.map((p, index, list) => (
                        <Box key={p.id} sx={{ display: 'grid', gridTemplateColumns: columns, columnGap: 0.75, alignItems: 'center' }}>
                          <TextField size="small" value={p.number} placeholder="#" inputProps={{ 'aria-label': '職員號碼' }} onChange={(e) => commit(updateOfficial(state, rosterTeam, p.id, { number: e.target.value }))} />
                          <TextField size="small" value={p.name} placeholder="姓名" inputProps={{ 'aria-label': '職員姓名' }} onChange={(e) => commit(updateOfficial(state, rosterTeam, p.id, { name: e.target.value }))} />
                          <Stack direction="row" spacing={0}>
                            <IconButton size="small" aria-label="上移" disabled={index === 0} onClick={() => commit(moveOfficial(state, rosterTeam, p.id, -1))}><ArrowUpwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" aria-label="下移" disabled={index === list.length - 1} onClick={() => commit(moveOfficial(state, rosterTeam, p.id, 1))}><ArrowDownwardIcon fontSize="small" /></IconButton>
                            <IconButton size="small" aria-label="刪除職員" onClick={() => commit(removeOfficial(state, rosterTeam, p.id))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Box>
              </DialogContent>
            </>
          );
        })()}
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setRosterTeam(null)}>關閉</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={importOpen && rosterTeam != null} onClose={() => setImportOpen(false)} fullWidth maxWidth="sm">
        {rosterTeam && (() => {
          const teamName = rosterTeam === 'A' ? state.teamAName : state.teamBName;
          return (
            <>
              <DialogTitle component="div" sx={{ borderBottom: 1, borderColor: 'divider', py: 1.75 }}>
                <Typography variant="h6" fontWeight={800}>匯入名單</Typography>
                <Typography variant="body2" color="text.secondary">{teamName || `${rosterTeam}隊`}</Typography>
              </DialogTitle>
              <DialogContent sx={{ pt: '16px !important' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  每行一個：號碼 姓名，或號碼,姓名。空白欄會保留現有名單。
                </Typography>
                <Stack spacing={1.25}>
                  <Box>
                    <TextField fullWidth size="small" label="球員" multiline minRows={5} value={importPlayersText} onChange={(e) => setImportPlayersText(e.target.value)} placeholder="12 陳大文" />
                    <Button component="label" variant="outlined" size="small" sx={{ mt: 0.75 }}>
                      從檔案讀入球員
                      <input hidden type="file" accept=".csv,.txt,.tsv,text/plain" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        file.text().then((raw) => setImportPlayersText(raw));
                        e.target.value = '';
                      }} />
                    </Button>
                  </Box>
                  <TextField fullWidth size="small" label="球隊職員" multiline minRows={3} value={importOfficialsText} onChange={(e) => setImportOfficialsText(e.target.value)} placeholder="1 主教練" />
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Button onClick={() => setImportOpen(false)}>取消</Button>
                <Button variant="contained" color="secondary" onClick={() => {
                  commit(importRoster(state, rosterTeam, parseRosterText(importPlayersText), parseRosterText(importOfficialsText)));
                  setImportPlayersText('');
                  setImportOfficialsText('');
                  setImportOpen(false);
                }}>匯入</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
      <Dialog open={penalty != null} onClose={() => { setPenaltyEditing(false); setPenaltyId(null); }} fullWidth maxWidth="xs">
        {penalty && (
          <>
            <DialogTitle>{penalty.team}隊 {penalty.playerNo} 出場罰時</DialogTitle>
            <DialogContent>
              <Typography variant="h3" fontWeight={800} textAlign="center" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                {formatClock(penalty.remainingSec)}
              </Typography>
              <Typography textAlign="center" color="text.secondary" sx={{ mt: 0.5, mb: penaltyEditing ? 2 : 0 }}>
                {penalty.manual ? '自訂時間' : `${Math.round(penalty.durationSec / 60)} 分鐘`}
              </Typography>
              {penaltyEditing && (
                <Stack spacing={1.25}>
                  <TextField size="small" label="號碼" value={penaltyNo} onChange={(e) => setPenaltyNo(e.target.value)} />
                  <Stack direction="row" spacing={1}>
                    <TextField size="small" type="number" label="分" value={penaltyMin} onChange={(e) => setPenaltyMin(e.target.value)} sx={{ flex: 1 }} />
                    <TextField size="small" type="number" label="秒" value={penaltySec} onChange={(e) => setPenaltySec(e.target.value)} sx={{ flex: 1 }} />
                  </Stack>
                </Stack>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
              {penaltyEditing ? (
                <>
                  <Button onClick={() => setPenaltyEditing(false)}>返回</Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      const seconds = Math.max(0, (Number(penaltyMin) || 0) * 60 + (Number(penaltySec) || 0));
                      commit(updateSuspension(state, penalty.id, { playerNo: penaltyNo, remainingSec: seconds }, now));
                      setPenaltyEditing(false);
                    }}
                  >
                    儲存
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outlined" onClick={() => {
                    setPenaltyNo(penalty.playerNo);
                    setPenaltyMin(String(Math.floor(penalty.remainingSec / 60)));
                    setPenaltySec(String(penalty.remainingSec % 60));
                    setPenaltyEditing(true);
                  }}>編輯</Button>
                  <Button variant="outlined" onClick={() => { setMoveId(penalty.id); setMoveNo(''); setMoveError(null); setPenaltyEditing(false); setPenaltyId(null); }}>換邊</Button>
                  <Button variant="outlined" color="error" onClick={() => { commit(removeSuspension(state, penalty.id)); setPenaltyEditing(false); setPenaltyId(null); }}>撤銷</Button>
                  <Button onClick={() => { setPenaltyEditing(false); setPenaltyId(null); }}>關閉</Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={timeoutEdit != null} onClose={() => setTimeoutEdit(null)} fullWidth maxWidth="xs">
        <DialogTitle>{timeoutEdit}隊暫停</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            上半場用 T1。下半場先用 T2，之後隨時可用 T3。若 T2 未用而到了下半場最後 5 分鐘，該次暫停算 T3，T2 會變暗。點下面的 T 可更正顯示。
          </Typography>
          {timeoutEdit && (
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {timeoutShown(state, timeoutEdit).map((on, index) => (
                <Button key={index} variant={on ? 'contained' : 'outlined'} color="secondary" onClick={() => commit(toggleTimeoutShown(state, timeoutEdit, index))} sx={{ minWidth: 64, fontWeight: 800 }}>
                  {`T${index + 1}`}
                </Button>
              ))}
            </Stack>
          )}
          {timeoutEdit && state.timeoutKind === timeoutEdit && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="剩餘秒數"
              value={timeoutRemainDraft}
              onChange={(e) => setTimeoutRemainDraft(e.target.value)}
              inputProps={{ min: 0, max: 60 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {timeoutEdit && state.timeoutKind === timeoutEdit && (
            <Button color="inherit" onClick={() => { commit(finishTeamTimeout(state)); setTimeoutEdit(null); }}>結束暫停</Button>
          )}
          {timeoutEdit && state.timeoutKind === timeoutEdit && (
            <Button variant="contained" color="secondary" onClick={() => { commit(setTimeoutRemaining(state, Number(timeoutRemainDraft) || 0, now)); setTimeoutEdit(null); }}>儲存時間</Button>
          )}
          <Button onClick={() => setTimeoutEdit(null)}>關閉</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveId != null} onClose={() => setMoveId(null)} fullWidth maxWidth="xs">
        <DialogTitle>換到對方</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            輸入對方球員號碼。號碼必須已在對方名單。
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="對方號碼"
            value={moveNo}
            onChange={(e) => { setMoveNo(e.target.value); setMoveError(null); }}
            error={Boolean(moveError)}
            helperText={moveError || ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveId(null)}>取消</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              if (!moveId) return;
              const result = moveSubclock(state, moveId, moveNo);
              if (result.error) {
                setMoveError(result.error);
                return;
              }
              commit(result.state);
              setMoveId(null);
            }}
          >
            確定
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>新增副時鐘</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <ToggleButtonGroup exclusive fullWidth size="small" value={addSide} onChange={(_, value) => value && setAddSide(value)}>
              <ToggleButton value="A">{state.teamAName || 'A隊'}</ToggleButton>
              <ToggleButton value="B">{state.teamBName || 'B隊'}</ToggleButton>
            </ToggleButtonGroup>
            <TextField label="球員號碼" value={addNo} onChange={(e) => { setAddNo(e.target.value); setAddError(null); }} helperText="號碼必須已在所選一隊" />
            <Stack direction="row" spacing={1}>
              <TextField label="分" type="number" value={addMin} onChange={(e) => setAddMin(e.target.value)} sx={{ flex: 1 }} />
              <TextField label="秒" type="number" value={addSec} onChange={(e) => setAddSec(e.target.value)} sx={{ flex: 1 }} />
            </Stack>
            {addError && <Typography color="error" variant="body2">{addError}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>取消</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              const seconds = (Number(addMin) || 0) * 60 + (Number(addSec) || 0);
              const result = addManualSubclock(state, addSide, addNo, seconds, now);
              if (result.error) {
                setAddError(result.error);
                return;
              }
              commit(result.state);
              setAddOpen(false);
              setAddNo('');
            }}
          >
            加入
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
